import { useNavigate } from 'react-router-dom';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Chip,
  Card,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Image as ImageIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  LocalShipping as LocalShippingIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import type { Activity, GroupAwareActivityResponse } from '../../../types/activity';
import type { Shipment } from '../../../types/shipment';
import { productService } from '../../../services/productService';
import { shipmentService } from '../../../services/shipmentService';
import { clearEditActivityDraft } from '../../../utils/activityDraftStorage';
import { shipmentStatusLabel, shipmentStatusColor } from '../../../utils/shipmentStatus';
import { formatDateTime } from '../../../utils/dateTimeUtils';
import ArchivedChip from '../../common/components/ArchivedChip';
import {
  ImagePreviewDialog,
  ProductThumbnail,
} from '../../common/components';

interface ActivityListProps {
  groupItems: GroupAwareActivityResponse[];
  companyId: number;
  onDeleteGroup?: (groupId: number) => void;
  onRestoreGroup?: (groupId: number) => void;
}

export default function ActivityList({
  groupItems,
  companyId,
  onDeleteGroup,
  onRestoreGroup,
}: ActivityListProps) {
  const navigate = useNavigate();

  // balanceMap: productId -> stockId -> current quantity + average unit cost
  const [balanceMap, setBalanceMap] = useState<Record<number, Record<number, { quantity: number; averageUnitCost?: number }>>>({});

  const [balanceError, setBalanceError] = useState<string | null>(null);

  // shipmentByGroupId: activityGroupId -> shipment (ttn/status), for the badge next to "Створити ТТН"
  const [shipmentByGroupId, setShipmentByGroupId] = useState<Record<number, Shipment>>({});

  // groupMenuAnchor: activityGroupId -> anchor element of its open kebab menu
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<{ groupId: number; anchorEl: HTMLElement } | null>(null);

  // previewImage: product photo currently opened in the full-size preview
  const [previewImage, setPreviewImage] = useState<{ id: number; name: string } | null>(null);

  const productIdsKey = useMemo(
    () => [...new Set(groupItems.flatMap(g => g.activities.map(a => a.productId)))].sort().join(','),
    [groupItems]
  );

  const groupIdsKey = useMemo(
    () => groupItems.map(g => g.activityGroup.id).sort((a, b) => a - b).join(','),
    [groupItems]
  );

  useEffect(() => {
    if (!productIdsKey) return;
    let cancelled = false;
    const productIds = productIdsKey.split(',').map(Number);
    productService.getAvailability(companyId, productIds).then(availList => {
      if (cancelled) return;
      const map: Record<number, Record<number, { quantity: number; averageUnitCost?: number }>> = {};
      for (const avail of availList) {
        map[avail.productId] = {};
        for (const dist of avail.stockDistribution) {
          map[avail.productId][dist.stockId] = { quantity: dist.quantity, averageUnitCost: dist.averageUnitCost };
        }
      }
      setBalanceMap(map);
      setBalanceError(null);
    }).catch(err => {
      if (cancelled) return;
      console.error('Не вдалося завантажити залишки товарів:', err);
      setBalanceMap({});
      setBalanceError('Не вдалося завантажити залишки товарів');
    });
    return () => {
      cancelled = true;
    };
  }, [productIdsKey, companyId]);

  useEffect(() => {
    if (!groupIdsKey) return;
    const groupIds = groupIdsKey.split(',').map(Number);
    shipmentService.getByActivityGroupIds(groupIds).then(shipments => {
      const map: Record<number, Shipment> = {};
      for (const shipment of shipments) {
        map[shipment.activityGroupId] = shipment;
      }
      setShipmentByGroupId(map);
    });
  }, [groupIdsKey]);

  const getOperationTypeColor = (operationType: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const typeColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      STOCK_IN: 'success',
      STOCK_OUT: 'error',
      BETWEEN_STOCKS: 'info',
    };
    return typeColors[operationType] || 'default';
  };

  const handleSerialNumberClick = (activity: Activity) => {
    navigate(`/activities/${activity.id}/product-serial-numbers`);
  };

  const handleOpenPreview = (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation();
    setPreviewImage({ id, name });
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  const handleBalanceClick = (productId: number) => {
    navigate(`/companies/${companyId}/stocks?products=${productId}`, {
      state: { returnPath: window.location.pathname + window.location.search },
    });
  };

  const renderActivity = (activity: Activity, isGroupArchived: boolean) => {
    const amountColor = getOperationTypeColor(activity.operationType);
    const amountSx = amountColor === 'default' ? {} : { color: `${amountColor}.main` };
    const stockBalance = activity.stockId ? balanceMap[activity.productId]?.[activity.stockId] : undefined;
    const counterpartyName = activity.clientName || activity.providerName;
    const counterpartyOrg = activity.clientOrganizationName || activity.providerOrganizationName;

    return (
      <Card key={activity.id} variant="outlined" sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5 }}>
          {activity.productMainImageId ? (
            <ProductThumbnail
              imageId={activity.productMainImageId}
              size={56}
              alt={activity.productName}
              onClick={(e) => handleOpenPreview(e, activity.productMainImageId!, activity.productName)}
            />
          ) : (
            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'grey.200' }}>
              <ImageIcon sx={{ color: 'grey.400' }} />
            </Avatar>
          )}

          {/* Blocks stack on mobile and sit in one row from md */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: 'block', md: 'grid' },
              gridTemplateColumns: {
                md: 'minmax(0, 1.6fr) minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 1.6fr)',
              },
              columnGap: { md: 3 },
              alignItems: { md: 'center' },
            }}
          >
            {/* Product */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2, flex: 1, minWidth: 0 }}>
                  {activity.productName}
                </Typography>
                <Tooltip title="Керування серійними номерами">
                  <IconButton
                    size="small"
                    onClick={() => handleSerialNumberClick(activity)}
                    sx={{ color: 'primary.main', flexShrink: 0, display: { md: 'none' } }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1 }}>SN</Typography>
                  </IconButton>
                </Tooltip>
              </Box>
              <Chip
                label={activity.activityTypeName}
                color={amountColor}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>

            {/* Amount, price, margin */}
            <Box sx={{ mt: { xs: 0.5, md: 0 } }}>
              <Typography variant="body2" noWrap>
                <Box component="span" sx={{ fontWeight: 700, ...amountSx }}>
                  {activity.amount} шт
                </Box>
                {activity.unitPrice != null && (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {' '}× ${activity.unitPrice.toFixed(2)} ={' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      ${(activity.totalPrice ?? 0).toFixed(2)}
                    </Box>
                  </Box>
                )}
              </Typography>
              {!isGroupArchived && activity.margin != null && (
                <Tooltip title={`Собівартість: $${activity.unitCostBasis?.toFixed(2)} / шт`}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      fontWeight: 600,
                      color: activity.margin >= 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    Маржа {activity.margin >= 0 ? '+' : ''}${activity.margin.toFixed(2)}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {/* Stock */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 0.5, md: 0 } }}>
              <StoreIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
              {activity.stockFromName ? `${activity.stockFromName} → ${activity.stockName}` : activity.stockName}
              {!isGroupArchived && stockBalance !== undefined && (
                <Tooltip
                  title={
                    stockBalance.averageUnitCost != null
                      ? `Поточний залишок на складі, середня собівартість $${stockBalance.averageUnitCost.toFixed(2)} / шт`
                      : 'Поточний залишок на складі (не на момент активності)'
                  }
                >
                  <Box
                    component="span"
                    onClick={() => handleBalanceClick(activity.productId)}
                    sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Залишок: {stockBalance.quantity}
                  </Box>
                </Tooltip>
              )}
            </Typography>

            {/* Counterparty + SN (desktop) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: { xs: 0.25, md: 0 } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {counterpartyName && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {activity.clientName
                        ? <PersonIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                        : <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />}
                      {counterpartyName}
                    </Typography>
                    {counterpartyOrg && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 2.25 }}>
                        {counterpartyOrg}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              <Tooltip title="Керування серійними номерами">
                <IconButton
                  size="small"
                  onClick={() => handleSerialNumberClick(activity)}
                  sx={{ color: 'primary.main', flexShrink: 0, display: { xs: 'none', md: 'inline-flex' } }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1 }}>SN</Typography>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  // Група відповідає бізнес-правилу створення ТТН (doc/shipping/novaposhta-integration.md#business-rules):
  // хоча б одна STOCK_OUT активність із клієнтом, і всі такі активності належать одному клієнту.
  // Остаточна перевірка все одно відбувається на BE — тут лише щоб не показувати кнопку даремно.
  const isShippingEligible = (item: GroupAwareActivityResponse): boolean => {
    const stockOutClientIds = new Set(
      item.activities
        .filter((a) => a.operationType === 'STOCK_OUT' && a.clientId)
        .map((a) => a.clientId)
    );
    return stockOutClientIds.size === 1;
  };

  const renderGroupItem = (item: GroupAwareActivityResponse) => {
    // Група завжди присутня (NOT NULL в БД)
    const group = item.activityGroup;
    const isArchived = !!group.deactivatedDate;
    const shipment = shipmentByGroupId[group.id];
    const isMenuOpen = groupMenuAnchor?.groupId === group.id;
    const shipmentPath = `/companies/${companyId}/activities/group/${group.id}/shipment`;

    const handleEditGroup = () => {
      clearEditActivityDraft(companyId, group.id);
      navigate(`/companies/${companyId}/activities/group/${group.id}/edit`);
    };

    return (
      <Paper key={group.id} sx={{ p: { xs: 1, sm: 2 }, mb: 2, backgroundColor: 'background.default' }}>
        {/* Group header */}
        <Box
          sx={{
            p: 1,
            mb: 1,
            backgroundColor: 'white',
            borderRadius: 1,
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
            >
              {group.description || 'Без опису'}
            </Typography>
            {isArchived && <ArchivedChip />}
            {!isArchived && shipment && (
              <Tooltip title={`ТТН ${shipment.ttnNumber} — натисніть, щоб оновити статус`}>
                <Chip
                  label={shipmentStatusLabel(shipment.status)}
                  color={shipmentStatusColor(shipment.status)}
                  size="small"
                  onClick={() => navigate(shipmentPath)}
                />
              </Tooltip>
            )}
            {!isArchived && !shipment && isShippingEligible(item) && (
              <Tooltip title="Створити ТТН">
                <IconButton size="small" color="primary" onClick={() => navigate(shipmentPath)}>
                  <LocalShippingIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isArchived && (
              <Tooltip title="Редагувати групу">
                <IconButton size="small" aria-label="Редагувати групу" onClick={handleEditGroup}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              aria-label="Меню дій з групою"
              onClick={(e) => setGroupMenuAnchor({ groupId: group.id, anchorEl: e.currentTarget })}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={isMenuOpen ? groupMenuAnchor.anchorEl : null}
              open={isMenuOpen}
              onClose={() => setGroupMenuAnchor(null)}
            >
              {isArchived ? (
                <MenuItem
                  onClick={() => {
                    setGroupMenuAnchor(null);
                    onRestoreGroup?.(group.id);
                  }}
                >
                  <ListItemIcon>
                    <RestoreIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Відновити групу</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() => {
                    setGroupMenuAnchor(null);
                    onDeleteGroup?.(group.id);
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>Видалити групу</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>

          {(group.paidByClientAmount > 0 || group.paidToProviderAmount > 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
              {group.paidByClientAmount > 0 && (
                <Chip
                  label={`Клієнт оплатив одразу: $${group.paidByClientAmount.toFixed(2)}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {group.paidToProviderAmount > 0 && (
                <Chip
                  label={`Постачальнику сплачено одразу: $${group.paidToProviderAmount.toFixed(2)}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          <Box sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Створив {group.createdUserName ?? '—'} · {formatDateTime(group.createdDate)}
            </Typography>
            {group.updatedDate && (
              <Typography variant="caption" color="text.secondary" display="block">
                Оновив {group.updatedUserName ?? '—'} · {formatDateTime(group.updatedDate)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Activities */}
        <Box>
          {item.activities.map((activity: Activity) => renderActivity(activity, isArchived))}
        </Box>
      </Paper>
    );
  };

  if (groupItems.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Немає активностей
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ви можете створити нову активність натиснувши кнопку "Додати" у верхньому правому куті
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {balanceError && <Alert severity="error" sx={{ mb: 2 }}>{balanceError}</Alert>}
      {groupItems.map((item: GroupAwareActivityResponse) => (
        <Box key={item.activityGroup.id}>
          {renderGroupItem(item)}
        </Box>
      ))}

      <ImagePreviewDialog
        imageId={previewImage?.id ?? null}
        alt={previewImage?.name}
        onClose={handleClosePreview}
      />
    </Box>
  );
}
